/**
 * ChatSidebar
 * Lists conversations across 4 tabs: All, Rituality AI, Rituals, People.
 * Includes search, archive view, and new conversation actions.
 */

import {
  Archive,
  ArrowLeft,
  EllipsisVertical,
  HelpCircle,
  Plus,
  Search,
  Sparkles,
  Trash2,
} from "lucide-react"
import { useState } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import aiAvatarImg from "@/assets/icons/ai-conversation-avatar.png"
import peopleIcon from "@/assets/icons/people.svg"
import ritualityAiIcon from "@/assets/icons/rituality-ai.svg"
import ritualsLinkIcon from "@/assets/icons/rituals-link.svg"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useTranslation } from "@/language"
import { cn } from "@/lib/utils"
import {
  useAiConversations,
  useCreateAiConversation,
  useDeleteAiConversation,
  useUpdateAiConversation,
} from "../../hooks/useAiChat"
import { useDeleteDmConversation, useDmConversations } from "../../hooks/useDmChat"
import { useRitualGroupChats } from "../../hooks/useRitualGroupChat"
import { useAllConversations, useArchiveConversation } from "../../hooks/useUnifiedConversations"
import type { AiConversation } from "../../types/ai-chat.types"
import type { DmConversation } from "../../types/dm-chat.types"
import type { RitualGroupConversationListItem } from "../../types/ritual-group-chat.types"
import { classifyConversation, isExpertConversation } from "../../utils/conversationKind"
import { formatTimeAgo } from "../../utils/formatTimeAgo"
import { UserSearchModal } from "../dm/UserSearchModal"
import { ArchiveFilledIcon } from "./ArchiveFilledIcon"
import { ArchiveView } from "./ArchiveView"
import { ConversationListItem } from "./ConversationListItem"
import { ConversationSearch } from "./ConversationSearch"
import { UnreadBadge } from "./UnreadBadge"

type TabType = "all" | "ai" | "rituals" | "people"

interface TabDefinition {
  key: TabType
  label: string
  icon?: string
}

function AiConversationItem({ conversation }: { conversation: AiConversation }) {
  const { t } = useTranslation("chats")
  const navigate = useNavigate()
  const { id } = useParams()
  const isActive = id === conversation.id
  const isReflect = conversation.metadata?.type === "reflect"
  const deleteConversation = useDeleteAiConversation()
  const updateConversation = useUpdateAiConversation()

  const handleArchive = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    updateConversation.mutate(
      { id: conversation.id, data: { status: "archived" } },
      {
        onSuccess: () => {
          toast.success(t("common.toasts.conversationArchived"))
          if (isActive) navigate("/chats")
        },
        onError: () => toast.error(t("common.toasts.failedToArchive")),
      },
    )
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    deleteConversation.mutate(conversation.id, {
      onSuccess: () => {
        toast.success(t("common.toasts.conversationDeleted"))
        if (isActive) navigate("/chats")
      },
      onError: () => toast.error(t("common.toasts.failedToDelete")),
    })
  }

  return (
    <Link
      to={`/chats/ai/${conversation.id}`}
      className={cn(
        "group relative w-full flex flex-col gap-[10px] px-[20px] py-[15px] text-left transition-colors no-underline border-b border-border",
        isActive ? "bg-[var(--color-chat-active)]" : "hover:bg-accent",
      )}
    >
      {/* Top row: avatar + name + time/actions */}
      <div className="flex items-center h-[30px]">
        <div className="flex items-center gap-[10px] flex-1 min-w-0">
          <img
            src={aiAvatarImg}
            alt={t("chatSidebar.tabs.ritualityAi")}
            className="size-[30px] rounded-full shrink-0"
          />
          <div className="flex items-center gap-1 min-w-0">
            {isReflect && <Sparkles className="size-3.5 shrink-0 text-amber-400" />}
            <p className="text-[13px] font-semibold text-foreground truncate">
              {conversation.title || t("common.newChat")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-[13px] text-muted-foreground/70 group-hover:hidden">
            {formatTimeAgo(conversation.lastMessageAt || conversation.createdAt)}
          </span>
          <div className="hidden group-hover:flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={handleArchive}
              className="hover:bg-muted text-muted-foreground"
              title={t("common.archive")}
            >
              <Archive className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={handleDelete}
              className="hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
              title={t("common.delete")}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Message preview */}
      <p className="text-[13px] font-normal text-foreground leading-[25px] line-clamp-2">
        {conversation.lastMessagePreview || t("common.startConversation")}
      </p>

      {/* Type badge */}
      <div className="flex items-center gap-[5px]">
        <img src={ritualityAiIcon} alt="" className="size-[14px] text-[var(--color-chat-badge)]" />
        <span className="text-[13px] font-semibold text-[var(--color-chat-badge)]">
          Rituality AI
        </span>
      </div>
    </Link>
  )
}

function DmConversationItem({ conversation }: { conversation: DmConversation }) {
  const { t } = useTranslation("chats")
  const navigate = useNavigate()
  const { conversationId } = useParams()
  const isActive = conversationId === conversation.id
  const deleteConversation = useDeleteDmConversation()
  const archiveConversation = useArchiveConversation()

  const handleArchive = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    archiveConversation.mutate(conversation.id, {
      onSuccess: () => {
        toast.success(t("common.toasts.conversationArchived"))
        if (isActive) navigate("/chats")
      },
      onError: () => toast.error(t("common.toasts.failedToArchive")),
    })
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    deleteConversation.mutate(conversation.id, {
      onSuccess: () => {
        toast.success(t("common.toasts.conversationDeleted"))
        if (isActive) {
          navigate("/chats")
        }
      },
      onError: () => {
        toast.error(t("common.toasts.failedToDelete"))
      },
    })
  }

  const otherUser = conversation.otherUser

  return (
    <Link
      to={`/chats/dm/${conversation.id}`}
      className={cn(
        "group w-full flex items-center gap-3 px-4 py-3 text-left transition-colors no-underline border-b border-border",
        isActive ? "bg-[var(--color-chat-active)]" : "hover:bg-accent",
      )}
    >
      <Avatar
        src={otherUser?.profilePhoto || undefined}
        alt={otherUser?.name || t("common.unknownUser")}
        fallback={otherUser?.name || "?"}
        size="md"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] font-semibold text-foreground truncate">
            {otherUser?.name || t("common.unknownUser")}
            {otherUser?.username && (
              <span className="font-normal text-muted-foreground ml-1">@{otherUser.username}</span>
            )}
          </p>
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-[13px] text-muted-foreground/70 group-hover:hidden">
              {formatTimeAgo(conversation.lastMessageAt)}
            </span>
            <div className="hidden group-hover:flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={handleArchive}
                className="hover:bg-muted text-muted-foreground"
                title={t("common.archive")}
              >
                <Archive className="size-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={handleDelete}
                className="hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                title={t("common.delete")}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] text-muted-foreground truncate">
            {conversation.lastMessage?.content || t("common.noMessages")}
          </p>
          <UnreadBadge count={conversation.unreadCount} />
        </div>
      </div>
    </Link>
  )
}

function RitualGroupItem({ conversation }: { conversation: RitualGroupConversationListItem }) {
  const { t } = useTranslation("chats")
  const navigate = useNavigate()
  const location = useLocation()
  const route = `/chats/ritual/${conversation.id}`
  const isActive = location.pathname === route
  const archiveConversation = useArchiveConversation()
  // Three flavors share this row type — the subtitle has to disambiguate
  // them. The booking presence flips the row from "organizer chat" to
  // "expert chat"; the anchor decides whether the expert chat is for a
  // coaching session or a ritual booking.
  const subtitle = ((): string => {
    const kind = classifyConversation(conversation)
    switch (kind.kind) {
      case "organizer":
        return `${conversation.participantCount} organizers`
      case "expert_ritual":
        return t("common.ritualSession")
      case "expert_coaching":
        return t("common.coachingSession")
      default: {
        const _exhaustive: never = kind
        return _exhaustive
      }
    }
  })()

  const handleArchive = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    archiveConversation.mutate(conversation.id, {
      onSuccess: () => {
        toast.success(t("common.toasts.conversationArchived"))
        if (isActive) navigate("/chats")
      },
      onError: () => toast.error(t("common.toasts.failedToArchive")),
    })
  }

  return (
    <Link
      to={route}
      className={cn(
        "group relative w-full flex items-center gap-3 px-4 py-3 text-left transition-colors no-underline border-b border-border",
        isActive ? "bg-[var(--color-chat-active)]" : "hover:bg-accent",
      )}
    >
      {conversation.profilePhoto ? (
        <img
          src={conversation.profilePhoto}
          alt=""
          className="size-[30px] rounded-full object-cover shrink-0"
        />
      ) : (
        <div className="size-[30px] rounded-full bg-primary/20 flex items-center justify-center shrink-0">
          <img src={ritualsLinkIcon} alt="" className="size-4 opacity-70" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] font-semibold text-foreground truncate">{conversation.name}</p>
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-[13px] text-muted-foreground/40 group-hover:hidden">
              {formatTimeAgo(conversation.lastMessageAt)}
            </span>
            <div className="hidden group-hover:flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={handleArchive}
                className="hover:bg-muted text-muted-foreground"
                title={t("common.archive")}
              >
                <Archive className="size-3.5" />
              </Button>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] text-muted-foreground truncate">{subtitle}</p>
          <UnreadBadge count={conversation.unreadCount} />
        </div>
      </div>
    </Link>
  )
}

function detectActiveTab(pathname: string): TabType {
  if (pathname.includes("/chats/dm")) return "people"
  if (pathname.includes("/chats/ritual")) return "rituals"
  if (pathname.includes("/chats/ai")) return "ai"
  return "all"
}

export function ChatSidebar({ onCollapse }: { onCollapse?: () => void }) {
  const { t } = useTranslation("chats")
  const tabs: TabDefinition[] = [
    { key: "all", label: t("chatSidebar.tabs.all") },
    { key: "ai", label: t("chatSidebar.tabs.ritualityAi"), icon: ritualityAiIcon },
    { key: "rituals", label: t("chatSidebar.tabs.rituals"), icon: ritualsLinkIcon },
    { key: "people", label: t("chatSidebar.tabs.people"), icon: peopleIcon },
  ]
  const navigate = useNavigate()
  const location = useLocation()

  const [activeTab, setActiveTab] = useState<TabType>(detectActiveTab(location.pathname))
  const [showUserSearch, setShowUserSearch] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [showArchive, setShowArchive] = useState(false)

  const aiData = useAiConversations()
  const dmData = useDmConversations()
  const ritualGroupData = useRitualGroupChats()
  const allData = useAllConversations()
  const createAiConversation = useCreateAiConversation()

  const handleNewChat = async () => {
    if (activeTab === "people") {
      setShowUserSearch(true)
      return
    }

    // Reuse the most recent empty conversation if one exists so users don't
    // accumulate untitled stubs from repeated New Chat clicks.
    const existingEmpty = aiData.data?.conversations.find(
      c => c.title === null && c.lastMessagePreview === null && c.lastMessageAt === null,
    )
    if (existingEmpty) {
      navigate(`/chats/ai/${existingEmpty.id}`)
      return
    }

    try {
      const conversation = await createAiConversation.mutateAsync()
      navigate(`/chats/ai/${conversation.id}`)
    } catch {
      toast.error(t("common.toasts.failedToCreate"))
    }
  }

  const isLoading =
    activeTab === "all"
      ? allData.isLoading
      : activeTab === "ai"
        ? aiData.isLoading
        : activeTab === "rituals"
          ? // Rituals tab now reads from ritualGroupData only (booking-backed
            // session chats moved to People). Still gate on dmData to avoid a
            // cross-tab flash if a refetch is in flight.
            ritualGroupData.isLoading
          : // People tab merges plain DMs with session group chats
            // (bookingId != null). Wait for both sources -- otherwise the
            // "No messages yet" empty state can flash before session chats
            // resolve.
            dmData.isLoading || ritualGroupData.isLoading

  return (
    <>
      <div className="w-full md:w-[375px] shrink-0 border-r border-border min-[1445px]:bg-[#FFFFFF0D] flex flex-col h-full">
        {/* Header - matches Figma: + New Chat left, icons right */}
        <div className="h-[64px] px-4 flex items-center justify-between border-b border-border">
          {showArchive ? (
            <button
              type="button"
              onClick={() => setShowArchive(false)}
              className="flex items-center gap-1.5 text-foreground hover:opacity-80 transition-opacity"
            >
              <ArrowLeft className="size-5" />
              <span className="text-[13px] font-semibold">{t("common.archive")}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNewChat}
              disabled={createAiConversation.isPending}
              className="flex items-center gap-1.5 text-foreground hover:opacity-80 transition-opacity"
            >
              <Plus className="size-5" />
              <span className="text-[13px] font-semibold">{t("common.newChat")}</span>
            </button>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowSearch(true)}
              className="text-foreground hover:opacity-70 transition-opacity"
              title={t("common.searchConversations")}
            >
              <Search className="size-5" />
            </button>
            <button
              type="button"
              onClick={() => setShowArchive(!showArchive)}
              className="text-foreground hover:opacity-70 transition-opacity"
              title={t("common.archivedConversations")}
            >
              {showArchive ? (
                <ArchiveFilledIcon className="size-5" />
              ) : (
                <Archive className="size-5" />
              )}
            </button>
            {onCollapse && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="text-foreground hover:opacity-70 transition-opacity md:hidden"
                    title={t("common.moreOptions")}
                  >
                    <EllipsisVertical className="size-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate("/settings?section=help-center")}>
                    <HelpCircle className="size-4" />
                    Help Center
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        {/* Tabs - with icons per Figma */}
        {!showArchive && (
          <div className="flex h-[50px]">
            {tabs.map(tab => {
              const isActive = activeTab === tab.key
              const isAll = tab.key === "all"
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    "flex items-center justify-center gap-[5px] h-[50px] text-[13px] whitespace-nowrap transition-colors",
                    isAll ? "w-[48px] shrink-0" : "flex-1",
                    isActive
                      ? "text-foreground font-semibold border-b border-foreground bg-[var(--color-chat-tab-active-bg)]"
                      : "text-muted-foreground font-normal border-b border-border hover:opacity-70",
                  )}
                >
                  {tab.icon && (
                    <img
                      src={tab.icon}
                      alt=""
                      className={cn("size-[20px] shrink-0", !isActive && "opacity-40")}
                    />
                  )}
                  {tab.label}
                </button>
              )
            })}
          </div>
        )}

        {/* Search bar (inline below tabs) */}
        {showSearch && <ConversationSearch onClose={() => setShowSearch(false)} />}

        {/* Archive view (inline below tabs) */}
        {showArchive && !showSearch && <ArchiveView />}

        {/* Conversation list (hidden when searching or viewing archive) */}
        {!showSearch && !showArchive && (
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="size-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : activeTab === "all" ? (
              allData.data?.conversations.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground">No conversations yet</p>
                </div>
              ) : (
                allData.data?.conversations.map(conversation => (
                  <ConversationListItem
                    key={`${conversation.type}-${conversation.id}`}
                    conversation={conversation}
                    showTypeBadge
                  />
                ))
              )
            ) : activeTab === "ai" ? (
              aiData.data?.conversations.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground">No conversations yet</p>
                </div>
              ) : (
                aiData.data?.conversations.map(conversation => (
                  <AiConversationItem key={conversation.id} conversation={conversation} />
                ))
              )
            ) : activeTab === "rituals" ? (
              (() => {
                // Rituals tab: organizer-only ritual group chats. Per-booking
                // expert / coaching session chats live under People (they're
                // a 1:1 with the expert, not a multi-participant ritual chat).
                // classifyConversation treats a missing bookingId as organizer,
                // matching the previous `bookingId == null` routing.
                const ritualGroupItems = (ritualGroupData.data?.conversations ?? []).filter(
                  c => classifyConversation(c).kind === "organizer",
                )
                if (ritualGroupData.isError) {
                  return (
                    <div className="text-center py-8 px-4">
                      <p className="text-sm text-destructive">Couldn't load ritual chats</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        Try refreshing the page.
                      </p>
                    </div>
                  )
                }
                if (ritualGroupItems.length === 0) {
                  return (
                    <div className="text-center py-8 px-4">
                      <p className="text-sm text-muted-foreground">No ritual chats yet</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        Group chats appear when a ritual organizer creates one.
                      </p>
                    </div>
                  )
                }
                return ritualGroupItems.map(conversation => (
                  <RitualGroupItem key={conversation.id} conversation={conversation} />
                ))
              })()
            ) : (
              (() => {
                // People tab: every 1:1 conversation. That's plain DMs, any
                // ritual-scoped DMs, and per-booking session group chats
                // (which are 1:1 between the booker and the expert). The two
                // expert flavors classify as non-organizer; null/undefined
                // bookingId classifies as organizer and routes to Rituals.
                const dms = dmData.data?.conversations ?? []
                const sessionGroupChats = (ritualGroupData.data?.conversations ?? []).filter(c =>
                  isExpertConversation(c),
                )
                // Surface a partial-failure banner if the session source
                // failed but we still have DMs to render -- otherwise
                // session chats silently disappear from the user's view.
                const sessionsLoadFailed = ritualGroupData.isError
                if (dms.length === 0 && sessionGroupChats.length === 0 && !sessionsLoadFailed) {
                  return (
                    <div className="text-center py-8 px-4">
                      <p className="text-sm text-muted-foreground">No messages yet</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        Visit a profile and tap Chat to start a conversation
                      </p>
                    </div>
                  )
                }
                return (
                  <>
                    {sessionsLoadFailed && (
                      <div className="px-4 py-2 text-xs text-destructive border-b border-border">
                        Couldn't load session chats. Try refreshing the page.
                      </div>
                    )}
                    {sessionGroupChats.map(conversation => (
                      <RitualGroupItem key={conversation.id} conversation={conversation} />
                    ))}
                    {dms.map(conversation => (
                      <DmConversationItem key={conversation.id} conversation={conversation} />
                    ))}
                  </>
                )
              })()
            )}
          </div>
        )}
      </div>

      <UserSearchModal open={showUserSearch} onOpenChange={setShowUserSearch} />
    </>
  )
}
